import React, { useEffect, useState } from 'react';
import { Button, notification, Space, Table } from 'antd';
import type { TableProps } from 'antd';
import { IUnit } from '@/interfaces/unit';
import { EditFilled, DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { deleteUnit, queryUnits } from '@/adapters/unit';
import { useMutation } from '@tanstack/react-query';
import UnitModal from './components/unit-modal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { IDataModal } from '@/interfaces/dataModal';
import Title from '@/components/title';
import SubMenu from '@/components/menu/sub-menu';
import { settingMenu } from '@/configs/menuItems';
import Card from '@/components/card';
import { getPathSegment } from '@/utils/pathUtils';

const _unitModal: IDataModal<IUnit> = {
  open: false,
};

const UnitSetting = () => {
  const pathSegment = getPathSegment();
  const [unit, setUnit] = useState<IUnit[]>([]);
  const [dataModal, setDataModal] = useState<IDataModal<IUnit>>(_unitModal);
  const [deleteModal, setDeleteModal] = useState<IDataModal<IUnit>>(_unitModal);
  const mutations = useMutation({
    mutationFn: async () => await queryUnits(),
    mutationKey: ['fetchUnit'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        setUnit(data.data);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const fetchUnit = async () => {
    try {
      mutations.mutateAsync();
    } catch (err) {
      console.error(err);
    }
  };

  const onActionUnit = () => {
    const message = dataModal?.id ? 'อัพเดทหน่วยสำเร็จ' : 'สร้างหน่วยสำเร็จ';
    notification.success({
      message: message,
    });
    setDataModal({ ..._unitModal });
    fetchUnit();
  };

  const onDeleteUnit = async () => {
    await deleteUnit(deleteModal?.id?.toString() || '');
    fetchUnit();
    setDeleteModal({ ..._unitModal });
  };

  useEffect(() => {
    fetchUnit();
  }, []);

  const columns: TableProps<IUnit>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      className: 'text-center',
      fixed: 'left',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'หน่วย',
      dataIndex: 'name',
      key: 'name',
      className: 'font-medium',
    },
    {
      title: 'หน่วย (ไทย)',
      dataIndex: 'nameTH',
      key: 'nameTH',
      className: 'font-medium',
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          <DeleteFilled
            className="text-red-700"
            onClick={() =>
              setDeleteModal({
                open: true,
                item: rec,
                id: rec.name,
              })
            }
          />
          <EditFilled
            className="text-blue-500"
            onClick={() =>
              setDataModal({
                open: true,
                item: rec,
                id: rec.name,
              })
            }
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <SubMenu active={pathSegment} items={settingMenu} />
      <Card>
        <Title title="จัดการหน่วย">
          <Button
            type="primary"
            className="max-w-[90px]"
            onClick={() => {
              setDataModal({
                ...dataModal,
                open: true,
              });
            }}
          >
            <PlusOutlined /> เพิ่มใหม่
          </Button>
        </Title>
        <div className="flex flex-col gap-3">
          <Table
            loading={mutations.isPending}
            rowKey={r => r.name}
            columns={columns}
            dataSource={unit || []}
            size="small"
            bordered
            className="font-ibm"
            pagination={{
              pageSize: 10,
            }}
          />
          <UnitModal
            handleCancel={() => {
              setDataModal({ ..._unitModal });
            }}
            handleOk={() => {
              onActionUnit();
            }}
            dataModal={dataModal}
          />
          <DeleteConfirmModal
            content={`คุณต้องการลบหน่วย '${deleteModal?.item?.name}' ใช่หรือไม่`}
            handleCancel={() => {
              setDeleteModal({ ..._unitModal });
            }}
            handleOk={() => {
              onDeleteUnit();
            }}
            open={deleteModal?.open}
            title={`หน่วย '${deleteModal?.item?.name}'`}
          />
        </div>
      </Card>
    </>
  );
};

export default UnitSetting;
